import dayjs from 'dayjs';
import strings from '../../Globalization';

/**
 * Generates print details parameters from the provided data
 * @param {Object} options - The options object
 * @param {Object} options.data - The data object containing cycle information
 * @param {Array|Object} options.data.TopSection - Section containing header information
 * @param {string} options.data.CycleTape - Cycle tape data
 * @param {string} options.data.PrinterLines - Printer lines data
 * @param {string} options.data.EquipmentName - Name of the equipment
 * @param {string} options.data.EquipmentTypeName - Type of equipment
 * @param {string} options.data.SerialNumber - Equipment serial number
 * @param {string} options.data.ModelName - Equipment model name
 * @param {string} options.data.LocationName - Equipment location
 * @param {string} options.data.CycleCount - Cycle count
 * @param {string} options.data.CycleStart - Cycle start date/time
 * @param {Array} options.data.CriticalParameters - Array of critical parameters
 * @param {string} options.data.CycleImageUrl - URL of cycle image
 * @param {string} options.data.CycleName - Name of the cycle
 * @param {Object} options.dateTimeFormat - Date/time format object
 * @param {string} options.dateTimeFormat.datetime - DateTime format string
 * @returns {Array} Array of request parameters for printing
 */
export const getPrintDetailsParams = ({ data, dateTimeFormat }) => {
	let topSection = data.TopSection;

	// Need to flat the array based on the condition of DataProperty.
	const flattenedItems = [];
	for (const param of topSection) {
		if (Array.isArray(param)) {
			flattenedItems.push(...param);
		} else if (param.DataProperty) {
			flattenedItems.push(param);
		}
	}

	topSection = flattenedItems.map((item) => ({
		...item,
		Label: strings[item.HeaderLabel]|| item.HeaderLabel,
		Value: item.Value,
		Image: item.Value || '',
		ImageColor: item.IsCustomRendering ? (item.Value.indexOf('times') > -1 ? 'red' : '#1ab394') : '',
		Index: item.Index >= 0 ? item.Index : -1
	}));
	const printData = data.CycleTape || data.PrinterLines || '';
	const pageInfo = [
		{ Property: 'ReportTitle', Value: strings.CycleLogs || '', LangString: strings.CycleLogs },
		{ Property: 'EquipmentName', Value: data.EquipmentName || '', LangString: strings.EquipmentName },
		{ Property: 'EquipmentType', Value: data.EquipmentTypeName || '', LangString: strings.EquipmentType },
		{ Property: 'SerialNumber', Value: data.SerialNumber || '', LangString: strings.SerialNumber },
		{ Property: 'ModelName', Value: data.ModelName || '', LangString: strings.ModalName },
		{ Property: 'Location', Value: data.LocationName || '', LangString: strings.LocationName },
		{ Property: 'CycleCount', Value: data.CycleCount || '', LangString: strings.CycleCount },
		{
			Property: 'CycleDate',
			Value: dayjs(data.CycleStart).format(dateTimeFormat.datetime) || '',
			LangString: strings.CycleDateTime
		},
		{ Property: 'Parameter', LangString: strings.Parameter },
		{ Property: 'Metric', LangString: strings.Metric },
		{ Property: 'Actual', LangString: strings.Actual },
		{ Property: 'CriticalParamenterTitle', LangString: strings.CriticalParamenterTitle }
	];

	const criticalParameters = data.CriticalParameters.map((obj) => ({
		HeaderLabel: obj.HeaderLabel,
		SubHeaderLabel: obj.SubHeaderLabel || '',
		Index: obj.Index || 0,
		UnitHeader: obj.UnitHeader || '',
		UnitSubHeader: obj.UnitSubHeader || '',
		ColSpan: obj.ColSpan || 0,
		RowSpan: obj.RowSpan || 0,
		Metric: obj.Metric || '',
		Actual: obj.Actual || '',
		CriticalParameterIcon: obj.CriticalParameterIcon,
		Control: obj.Control || '',
		Independent: obj.Independent || '',
		TabHeader: !obj.isTitle || obj.TabHeader || false
	}));
	const requestParameters = [
		{ Name: 'action', Value: 'otherAction' },
		{ Name: 'otherAction', Value: 'printReport' },
		{ Name: 'cycleCount', Value: data.CycleCount },
		{ Name: 'imageUrl', Value: data.CycleImageUrl },
		{ Name: 'cycleName', Value: data.CycleName },
		{ Name: 'cycleTape', Value: printData },
		{ Name: 'topSection', Value: JSON.stringify(topSection) },
		{ Name: 'criticalParameters', Value: JSON.stringify(criticalParameters) },
		{ Name: 'pageInfo', Value: JSON.stringify(pageInfo) },
		{ Name: 'isWasherOrLCS', Value: true }
	];

	return requestParameters;
};